// Case Buddy domain module generated from CaseBuddyCore.js.
// Spreadsheet-shaped rows remain the source of truth.
import {
  CASE_ENTITY_CURATOR_OVERLAY_STORAGE_KEY,
  CASE_ENTITY_KIND_EXCHANGE,
  CASE_ENTITY_KIND_NON_EXCHANGE,
  CASE_ENTITY_KIND_UNKNOWN,
  CASE_ENTITY_LOCAL_SUPPRESSED_TERMS,
  CASE_ENTITY_REVIEW_STATUS_KNOWN,
  CASE_ENTITY_REVIEW_STATUS_NEW,
  CASE_ENTITY_REVIEW_STATUS_SUPPRESSED
} from './CaseBuddyAppConstants.js';
import { caseBuddyHashString, normalizeCell, rowHasMeaningfulData } from './CaseBuddyShared.js';
import { normalizeCaseMatrix } from './CaseBuddyWorkbookState.js';

export function createDefaultEntityCuratorOverlay() {
  return {
    version: 1,
    exchangeAdditions: [],
    nonExchangeAdditions: [],
    exchangeRemovals: [],
    nonExchangeRemovals: [],
    suppressedTerms: [],
    updatedAtUtc: ''
  };
}

export function normalizeEntityCuratorList(value) {
  if (!Array.isArray(value)) return [];
  const seen = new Set();
  const result = [];

  value.forEach((item) => {
    const text = normalizeCell(item);
    if (!text || seen.has(text)) return;
    seen.add(text);
    result.push(text);
  });

  return result.sort((left, right) => left.localeCompare(right));
}

export function normalizeEntityCuratorOverlay(raw = {}) {
  const defaults = createDefaultEntityCuratorOverlay();
  const source = raw && typeof raw === 'object' ? raw : {};
  return {
    ...defaults,
    ...source,
    version: 1,
    exchangeAdditions: normalizeEntityCuratorList(source.exchangeAdditions),
    nonExchangeAdditions: normalizeEntityCuratorList(source.nonExchangeAdditions),
    exchangeRemovals: normalizeEntityCuratorList(source.exchangeRemovals),
    nonExchangeRemovals: normalizeEntityCuratorList(source.nonExchangeRemovals),
    suppressedTerms: normalizeEntityCuratorList(source.suppressedTerms),
    updatedAtUtc: normalizeCell(source.updatedAtUtc)
  };
}

export function loadEntityCuratorOverlayFromStorage() {
  if (typeof localStorage === 'undefined') return createDefaultEntityCuratorOverlay();

  try {
    return normalizeEntityCuratorOverlay(JSON.parse(localStorage.getItem(CASE_ENTITY_CURATOR_OVERLAY_STORAGE_KEY) || '{}'));
  } catch {
    return createDefaultEntityCuratorOverlay();
  }
}

export function saveEntityCuratorOverlayToStorage(overlay = {}) {
  const normalized = normalizeEntityCuratorOverlay({ ...overlay, updatedAtUtc: new Date().toISOString() });
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(CASE_ENTITY_CURATOR_OVERLAY_STORAGE_KEY, JSON.stringify(normalized));
  }
  return normalized;
}

export function addEntityCuratorTermToList(list = [], term = '') {
  const text = normalizeCell(term);
  if (!text) return normalizeEntityCuratorList(list);
  return normalizeEntityCuratorList([...(Array.isArray(list) ? list : []), text]);
}

export function removeEntityCuratorTermFromList(list = [], term = '') {
  const text = normalizeCell(term);
  return normalizeEntityCuratorList((Array.isArray(list) ? list : []).filter(item => normalizeCell(item) !== text));
}

export function buildEffectiveEntityCurators(remote = {}, overlay = {}) {
  const normalizedOverlay = normalizeEntityCuratorOverlay(overlay);
  const remoteExchange = normalizeEntityCuratorList(remote.exchange || remote.exchanges || []);
  const remoteNonExchange = normalizeEntityCuratorList(remote.nonExchange || remote.nonExchanges || []);
  const exchangeRemovalSet = new Set(normalizedOverlay.exchangeRemovals);
  const nonExchangeRemovalSet = new Set(normalizedOverlay.nonExchangeRemovals);
  const byName = new Map();

  const addItem = (name, kind, source) => {
    const term = normalizeCell(name);
    if (!term) return;
    const key = `${kind}|${term}`;
    if (!byName.has(key)) {
      byName.set(key, {
        id: `${kind}-${source}-${caseBuddyHashString(term)}`,
        name: term,
        kind,
        source,
        sourceLabel: source === 'local' ? 'Local' : 'Server',
        removed: false
      });
    }
  };

  remoteExchange.forEach((name) => {
    if (!exchangeRemovalSet.has(name)) addItem(name, CASE_ENTITY_KIND_EXCHANGE, 'remote');
  });
  remoteNonExchange.forEach((name) => {
    if (!nonExchangeRemovalSet.has(name)) addItem(name, CASE_ENTITY_KIND_NON_EXCHANGE, 'remote');
  });
  normalizedOverlay.exchangeAdditions.forEach(name => addItem(name, CASE_ENTITY_KIND_EXCHANGE, 'local'));
  normalizedOverlay.nonExchangeAdditions.forEach(name => addItem(name, CASE_ENTITY_KIND_NON_EXCHANGE, 'local'));

  const items = Array.from(byName.values()).sort((left, right) => {
    if (left.kind !== right.kind) return left.kind.localeCompare(right.kind);
    return left.name.localeCompare(right.name);
  });

  return {
    exchange: items.filter(item => item.kind === CASE_ENTITY_KIND_EXCHANGE),
    nonExchange: items.filter(item => item.kind === CASE_ENTITY_KIND_NON_EXCHANGE),
    all: items,
    suppressedTerms: normalizedOverlay.suppressedTerms.slice(),
    overlay: normalizedOverlay,
    remoteExchangeCount: remoteExchange.length,
    remoteNonExchangeCount: remoteNonExchange.length
  };
}

export function stripCaseEntityOuterPunctuation(value) {
  return normalizeCell(value).replace(/^[\s.,;:!?"'“”‘’`]+|[\s.,;:!?"'“”‘’`]+$/g, '').trim();
}

export function cleanupCaseEntityPhrase(value) {
  let text = stripCaseEntityOuterPunctuation(value);
  if (!text) return '';

  while (text.endsWith(')')) {
    const openIndex = text.lastIndexOf('(');
    if (openIndex < 0) break;
    text = text.slice(0, openIndex).trimEnd();
  }

  return stripCaseEntityOuterPunctuation(text);
}

export function normalizeCaseEntityTerm(value) {
  return cleanupCaseEntityPhrase(value)
    .toLowerCase()
    .replace(/[’‘`]/g, "'")
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function splitCaseEntityFragmentsPreservingDecimals(line = '') {
  const results = [];
  const source = normalizeCell(line);
  if (!source) return results;

  let current = '';
  for (let index = 0; index < source.length; index += 1) {
    const char = source[index];
    const previousChar = source[index - 1] || '';
    const nextChar = source[index + 1] || '';
    const isDecimalPoint = char === '.' && /\d/.test(previousChar) && /\d/.test(nextChar);
    const isDomainOrBrandPoint = char === '.' && /[A-Za-z0-9]/.test(previousChar) && /[A-Za-z0-9]/.test(nextChar);

    if ((char === '.' && !isDecimalPoint && !isDomainOrBrandPoint) || char === ';') {
      const fragment = cleanupCaseEntityPhrase(current);
      if (fragment) results.push(fragment);
      current = '';
      continue;
    }

    current += char;
  }

  const tail = cleanupCaseEntityPhrase(current);
  if (tail) results.push(tail);
  return results;
}

export function extractCaseEntityNoteCandidateFragments(notes = '') {
  const results = [];
  const source = String(notes || '').replace(/\r/g, '\n');
  if (!source.trim()) return results;

  source.split('\n').forEach((line) => {
    if (looksLikeCaseBuddyGeneratedEntityNoise(line)) return;
    splitCaseEntityFragmentsPreservingDecimals(line).forEach((fragment) => {
      let cleaned = cleanupCaseEntityPhrase(fragment);
      const lower = cleaned.toLowerCase();
      ['path ends', 'continues as', 'branches as'].forEach((marker) => {
        const markerIndex = lower.indexOf(marker);
        if (markerIndex >= 0) cleaned = cleanupCaseEntityPhrase(cleaned.slice(0, markerIndex));
      });
      if (cleaned && !looksLikeCaseBuddyGeneratedEntityNoise(cleaned)) results.push(cleaned);
    });
  });

  return results;
}

export function stripLeadingCaseEntityPreposition(value = '') {
  const text = cleanupCaseEntityPhrase(value);
  const lower = text.toLowerCase();
  for (const prefix of ['at ', 'on ', 'with ', 'from ', 'to ']) {
    if (lower.startsWith(prefix)) return cleanupCaseEntityPhrase(text.slice(prefix.length));
  }
  return text;
}

export function tryExtractCaseEntityFromRoleOwnedPhrase(value = '') {
  const text = String(value || '').replace(/[’‘`]/g, "'").trim();
  if (!text) return '';
  const lower = text.toLowerCase();
  const prefixes = [
    "the victim's ", "victim's ", "victims' ", 'victims ', 'victim ',
    "the suspect's ", "suspect's ", "suspects' ", 'suspects ', 'suspect ',
    "the scammer's ", "scammer's ", "scammers' ", 'scammers ', 'scammer ',
    "the sender's ", "sender's ", "senders' ", 'senders ', 'sender ',
    "the receiver's ", "receiver's ", "receivers' ", 'receivers ', 'receiver '
  ];

  for (const prefix of prefixes) {
    if (lower.startsWith(prefix)) return stripLeadingCaseEntityPreposition(text.slice(prefix.length));
  }
  return '';
}

export function tryExtractCaseEntityFromNarrative(value = '') {
  const text = cleanupCaseEntityPhrase(value);
  if (!text) return '';
  const lower = text.toLowerCase();
  const prefixes = [
    'funds move from the ', 'funds move into the ', 'funds move to the ',
    'funds move from ', 'funds move into ', 'funds move to ',
    'funds are sent to the ', 'funds are sent to ', 'funds are sent from the ', 'funds are sent from ',
    'the funds move into the ', 'the funds move into ', 'the funds move to the ', 'the funds move to ',
    'moved to the ', 'moved to ', 'transferred to the ', 'transferred to ',
    'sent into the ', 'sent into ', 'received by the ', 'received by '
  ];
  const endMarkers = [' address', ' wallet', ' deposit address', ' deposit', ',', ';', ':', '\n', '\r'];

  for (const prefix of prefixes) {
    if (!lower.startsWith(prefix)) continue;
    let endIndex = -1;
    for (const marker of endMarkers) {
      const markerIndex = lower.indexOf(marker, prefix.length);
      if (markerIndex > prefix.length && (endIndex === -1 || markerIndex < endIndex)) {
        endIndex = markerIndex;
      }
    }
    const extracted = endIndex > prefix.length ? text.slice(prefix.length, endIndex) : text.slice(prefix.length);
    const cleaned = cleanupCaseEntityPhrase(extracted);
    if (cleaned) return cleaned;
  }

  return '';
}

export function tryExtractCaseEntityFromGeneratedSystemPhrase(value = '') {
  const text = cleanupCaseEntityPhrase(value);
  if (!text) return '';

  const generatedPrefixes = [
    /^deposit address pattern\s*[:-]?\s+(.+)$/i,
    /^deposit pattern\s*[:-]?\s+(.+)$/i,
    /^possible deposit address pattern\s*[:-]?\s+(.+)$/i,
    /^high-priority deposit pattern\s*[:-]?\s+(.+)$/i,
    /^known entity casing\s*[:-]?\s+(.+)$/i
  ];

  for (const pattern of generatedPrefixes) {
    const match = text.match(pattern);
    if (!match?.[1]) continue;
    const cleaned = cleanupCaseEntityPhrase(match[1]);
    if (cleaned) return cleaned;
  }

  return '';
}

export function looksLikeCaseEntityAmountFragment(value = '') {
  const text = cleanupCaseEntityPhrase(value);
  if (!text) return false;
  return /^\$?[\d,]+(?:\.\d+)?(?:\s+(?:BTC|ETH|USDT|USDC|DAI|TRX|BNB|SOL|USD|EUR|CAD))?$/i.test(text) ||
    /^\(?follows\s+[\d,]+(?:\.\d+)?\s+[A-Z0-9]+\)?$/i.test(text);
}

export function looksLikeCaseEntityBlockchainAddress(value = '') {
  const text = cleanupCaseEntityPhrase(value);
  return /^0x[a-fA-F0-9]{40}$/.test(text) ||
    /^(?:41[a-fA-F0-9]{40}|T[1-9A-HJ-NP-Za-km-z]{33})$/.test(text) ||
    /^(?:bc1[ac-hj-np-z02-9]{11,71}|[13][a-km-zA-HJ-NP-Z1-9]{25,34})$/.test(text);
}

export function isGenericCaseEntityDescriptor(value = '') {
  const normalized = normalizeCaseEntityTerm(value);
  return new Set([
    'address', 'wallet', 'account', 'deposit', 'deposit address', 'deposit wallet',
    'exchange account', 'exchange wallet', 'crypto wallet', 'private address', 'private wallet',
    'personal address', 'personal wallet', 'receiving address', 'receiving wallet',
    'destination address', 'destination wallet', 'new address', 'new wallet', 'unknown address',
    'unknown wallet', 'dormant wallet', 'intermediary wallet', 'intermediate wallet',
    'victim address', 'victim wallet', 'suspect address', 'suspect wallet', 'scammer address',
    'scammer wallet', 'victims address', 'suspects address'
  ]).has(normalized);
}

export function looksLikeCaseEntityPathLabelHelper(value = '') {
  const text = cleanupCaseEntityPhrase(value);
  if (!text || text.length > 32) return false;
  if (/(?:->|→)/.test(text)) return true;
  if (/^\d+(?:[a-z][a-z0-9.]*)?$/i.test(text)) return true;
  if (/^[A-Z]{1,2}(?:\d+[a-z0-9.]*)?$/.test(text)) return true;
  return /^[A-Za-z0-9][A-Za-z0-9_:/.-]*-\d+(?:[a-z0-9.]*)?$/i.test(text);
}

export function looksLikeCaseBuddyGeneratedEntityNoise(value = '') {
  const text = cleanupCaseEntityPhrase(value);
  if (!text) return false;
  const lower = text.toLowerCase();
  return /^(?:deposit address pattern|deposit pattern|possible deposit address pattern|high-priority deposit pattern|known entity casing)\b/i.test(text) ||
    /\b(?:\d+\s*k\+\s*txns[_\s-]*check[_\s-]*if[_\s-]*entity|check[_\s-]*if[_\s-]*entity)\b/i.test(text) ||
    /^this transaction\b/i.test(text) ||
    /\bis the same transaction hash as\b/i.test(text) ||
    /^\d+\)\s+is the same transaction hash as\b/i.test(text) ||
    /\bsame transaction hash as\b/i.test(text) ||
    /^duplicate transaction(?: hash)?\b/i.test(text) ||
    /^original notes backup\b/i.test(text) ||
    /^hash replacement\b/i.test(text) ||
    lower === 'transaction hash' ||
    lower === 'transaction id' ||
    lower === 'txn id';
}

export function looksLikeCaseEntityTopLevelDomainFragment(value = '') {
  return new Set(['com', 'net', 'org', 'io', 'fi', 'app', 'xyz', 'co', 'exchange', 'crypto']).has(normalizeCaseEntityTerm(value));
}

export function getCaseEntitySuppressionReason(value = '', overlay = {}) {
  const text = cleanupCaseEntityPhrase(value);
  if (!text) return 'Blank candidate text.';
  const lower = text.toLowerCase();
  const normalized = normalizeCaseEntityTerm(text);
  const overlaySuppressed = new Set(normalizeEntityCuratorOverlay(overlay).suppressedTerms.map(term => normalizeCaseEntityTerm(term)));
  const builtInSuppressed = new Set(CASE_ENTITY_LOCAL_SUPPRESSED_TERMS.map(term => normalizeCaseEntityTerm(term)));

  if (overlaySuppressed.has(normalized)) return 'Locally suppressed term.';
  if (builtInSuppressed.has(normalized)) return 'Known non-entity noise.';
  if (looksLikeCaseBuddyGeneratedEntityNoise(text)) return 'Case Buddy-generated note text.';
  if (looksLikeCaseEntityTopLevelDomainFragment(text)) return 'Domain suffix fragment noise.';

  switch (lower) {
    case 'victim':
    case "victim's":
    case 'suspect':
    case "suspect's":
    case 'scammer':
    case "scammer's":
    case 'sender':
    case "sender's":
    case 'receiver':
    case "receiver's":
      return 'Generic narrative role text.';
    default:
      break;
  }

  if (looksLikeCaseEntityAmountFragment(text)) return 'Amount/token fragment noise.';
  if (looksLikeCaseEntityBlockchainAddress(text)) return 'Looks like a blockchain address.';
  if (isGenericCaseEntityDescriptor(text)) return 'Generic wallet/address descriptor.';
  if (looksLikeCaseEntityPathLabelHelper(text)) return 'Looks like path-label helper text.';
  if (/^(continues as|branches as|path ends)/i.test(text)) return 'Path helper text.';
  if (/^(?:a portion of the funds remains|a portion of the victim's funds remains|portion of the funds remains|funds remain at this address)/i.test(text)) return 'Narrative balance text.';
  if (/^(?:funds move from|funds move into|funds move to|funds are sent to|funds are sent from|the funds move into|the funds move to)/i.test(text)) return 'Narrative movement text.';
  if (text.split(/\s+/).filter(Boolean).length > 8) return 'Narrative text.';

  return '';
}

export function buildKnownEntityMatchFallbackTerms(value = '') {
  const text = cleanupCaseEntityPhrase(value);
  if (!text) return [];
  const suffixes = [
    ' exchange hot wallet address', ' exchange hot wallet', ' exchange address', ' exchange account', ' exchange wallet',
    ' hot wallet address', ' hot wallet', ' service address', ' service wallet', ' service account', ' service',
    ' account', ' deposit address', ' deposit wallet', ' deposit account', ' deposit',
    ' wallet address', ' wallet', ' address'
  ];
  const prefixes = [
    "the suspect's ", "suspect's ", "suspects' ", 'suspects ', 'suspect ',
    "the victim's ", "victim's ", "victims' ", 'victims ', 'victim '
  ];
  const results = [];
  const addResult = (candidate = '') => {
    const trimmed = cleanupCaseEntityPhrase(candidate.replace(/\s*#\s*\d+\s*$/i, ''));
    if (trimmed && trimmed !== text && !results.includes(trimmed)) results.push(trimmed);
    return trimmed;
  };

  const bases = [];
  const addBase = (candidate = '') => {
    const trimmed = addResult(candidate);
    if (trimmed && !bases.includes(trimmed)) bases.push(trimmed);
  };

  addBase(text);
  prefixes.forEach((prefix) => {
    if (text.toLowerCase().startsWith(prefix)) addBase(text.slice(prefix.length));
  });

  bases.slice().forEach((base) => {
    const lowerBase = base.toLowerCase();
    suffixes.forEach((suffix) => {
      if (lowerBase.endsWith(suffix) && base.length > suffix.length) {
        addBase(base.slice(0, base.length - suffix.length));
      }
    });
  });

  return results;
}

export function caseEntityWrapperTokens(value = '') {
  return normalizeCaseEntityTerm(value).split(/\s+/).filter(Boolean);
}

export function findCaseEntityGenericWrapperCuratorMatch(effective = {}, rawTerm = '') {
  const term = cleanupCaseEntityPhrase(rawTerm);
  const normalizedTerm = normalizeCaseEntityTerm(term);
  if (!normalizedTerm) return null;

  const all = Array.isArray(effective.all) ? effective.all : [];
  const termTokens = caseEntityWrapperTokens(term);
  const allowedWrapperTokens = new Set([
    'a', 'an', 'and', 'at', 'by', 'from', 'in', 'into', 'is', 'of', 'on', 'or', 'the', 'to', 'using', 'with',
    'account', 'address', 'addresses', 'deposit', 'destination', 'entity', 'exchange', 'hot', 'known',
    'pattern', 'potential', 'receiver', 'receiving', 'service', 'sender', 'source', 'wallet', 'wallets',
    'victim', 'victims', 'suspect', 'suspects', 'scammer', 'scammers'
  ]);

  const candidates = all
    .map(item => ({
      item,
      normalizedName: normalizeCaseEntityTerm(item?.name),
      nameTokens: new Set(caseEntityWrapperTokens(item?.name))
    }))
    .filter(({ normalizedName }) => normalizedName && normalizedName !== normalizedTerm && normalizedTerm.includes(normalizedName))
    .sort((left, right) => right.normalizedName.length - left.normalizedName.length);

  for (const { item, nameTokens } of candidates) {
    const leftoverTokens = termTokens.filter(token => !nameTokens.has(token));
    if (!leftoverTokens.length || leftoverTokens.every(token => allowedWrapperTokens.has(token))) {
      return {
        isMatch: true,
        isCaseMismatch: false,
        matchedName: item.name,
        entityKind: item.kind,
        source: item.source,
        candidate: term,
        matchedByWrapper: true
      };
    }
  }

  return null;
}

export function findCaseEntityCuratorMatch(effective = {}, rawTerm = '') {
  const term = cleanupCaseEntityPhrase(rawTerm);
  const all = Array.isArray(effective.all) ? effective.all : [];
  const terms = [term, ...buildKnownEntityMatchFallbackTerms(term)].filter(Boolean);

  for (const candidate of terms) {
    const exact = all.find(item => item.name === candidate);
    if (exact) {
      return { isMatch: true, isCaseMismatch: false, matchedName: exact.name, entityKind: exact.kind, source: exact.source, candidate };
    }
  }

  const normalizedTerms = new Set(terms.map(item => normalizeCaseEntityTerm(item)).filter(Boolean));
  const caseMismatch = all.find(item => normalizedTerms.has(normalizeCaseEntityTerm(item.name)));
  if (caseMismatch) {
    return { isMatch: false, isCaseMismatch: true, matchedName: caseMismatch.name, entityKind: caseMismatch.kind, source: caseMismatch.source, candidate: term };
  }

  const wrapperMatch = findCaseEntityGenericWrapperCuratorMatch(effective, term);
  if (wrapperMatch) return wrapperMatch;

  return { isMatch: false, isCaseMismatch: false, matchedName: '', entityKind: CASE_ENTITY_KIND_UNKNOWN, source: '', candidate: term };
}

export function getCaseEntityKnownTermFragmentSuppressionReason(value = '', effective = {}) {
  const normalized = normalizeCaseEntityTerm(value);
  if (!normalized || normalized.length < 3 || normalized.includes(' ')) return '';

  const genericFragments = new Set(['crypto', 'wallet', 'address', 'deposit', 'hot', 'com']);
  if (genericFragments.has(normalized)) return 'Known entity fragment/generic term noise.';

  const knownTerms = (Array.isArray(effective.all) ? effective.all : [])
    .map(item => normalizeCaseEntityTerm(item?.name))
    .filter(Boolean);

  if (knownTerms.some(term => term === normalized)) return '';

  const isKnownFragment = knownTerms.some((knownTerm) => {
    const knownWords = knownTerm.split(/\s+/).filter(Boolean);
    if (knownWords.includes(normalized)) return true;

    // Handles camel/punctuation-derived fragments such as "Crypto" from "Crypto.com"
    // without suppressing unrelated short text too aggressively.
    if (normalized.length < 4) return false;
    return knownWords.some(word => word.length >= 4 && (word.includes(normalized) || normalized.includes(word)));
  });

  return isKnownFragment ? 'Fragment of a known curator entity.' : '';
}

export function determineSuggestedCaseEntityKind(displayName = '', match = {}) {
  if (match?.isMatch && match.entityKind) return match.entityKind;
  const lower = cleanupCaseEntityPhrase(displayName).toLowerCase();
  const exchangeHints = ['deposit', 'exchange', 'hot wallet', 'coinbase', 'binance', 'kraken', 'okx', 'bybit', 'bitget', 'kucoin', 'mexc', 'gate', 'crypto com', 'poloniex', 'bitfinex', 'gemini', 'bitstamp', 'upbit', 'bittrex', 'htx'];
  const nonExchangeHints = ['private', 'personal', 'individual', 'victim', 'scammer', 'merchant', 'burn', 'treasury', 'contract', 'broker', 'team', 'fund', 'dao'];
  if (exchangeHints.some(hint => lower.includes(hint))) return CASE_ENTITY_KIND_EXCHANGE;
  if (nonExchangeHints.some(hint => lower.includes(hint))) return CASE_ENTITY_KIND_NON_EXCHANGE;
  return CASE_ENTITY_KIND_UNKNOWN;
}

export function formatCaseEntityKindLabel(kind = '') {
  if (kind === CASE_ENTITY_KIND_EXCHANGE) return 'Exchange';
  if (kind === CASE_ENTITY_KIND_NON_EXCHANGE) return 'Non-exchange';
  return 'Unknown';
}

export function evaluateCaseEntityObservation(row = [], rowIndex = 0, sourceField = '', rawPhrase = '', effective = {}, overlay = {}) {
  let cleanedPhrase = cleanupCaseEntityPhrase(rawPhrase);
  if (!cleanedPhrase) return null;

  let suppressionReason = looksLikeCaseBuddyGeneratedEntityNoise(cleanedPhrase)
    ? 'Case Buddy-generated note text.'
    : '';

  if (!suppressionReason) {
    const narrativeEntity = tryExtractCaseEntityFromNarrative(cleanedPhrase);
    if (narrativeEntity) cleanedPhrase = narrativeEntity;

    suppressionReason = getCaseEntitySuppressionReason(cleanedPhrase, overlay);
  }
  if (!suppressionReason) {
    const roleOwnedEntity = tryExtractCaseEntityFromRoleOwnedPhrase(cleanedPhrase);
    if (roleOwnedEntity) {
      cleanedPhrase = roleOwnedEntity;
      suppressionReason = getCaseEntitySuppressionReason(cleanedPhrase, overlay);
    }
  }

  const normalized = normalizeCaseEntityTerm(cleanedPhrase);
  let match = suppressionReason ? { isMatch: false, isCaseMismatch: false } : findCaseEntityCuratorMatch(effective, cleanedPhrase);

  if (!suppressionReason && !match.isMatch && !match.isCaseMismatch) {
    suppressionReason = getCaseEntityKnownTermFragmentSuppressionReason(cleanedPhrase, effective);
    if (suppressionReason) match = { isMatch: false, isCaseMismatch: false };
  }

  let status = CASE_ENTITY_REVIEW_STATUS_NEW;

  if (suppressionReason || !normalized) {
    status = CASE_ENTITY_REVIEW_STATUS_SUPPRESSED;
    suppressionReason = suppressionReason || 'Could not normalize candidate text.';
  } else if (match.isMatch) {
    status = CASE_ENTITY_REVIEW_STATUS_KNOWN;
  }

  const displayName = match.isMatch ? match.matchedName : cleanedPhrase;
  const candidateKey = normalized || cleanupCaseEntityPhrase(cleanedPhrase).toLowerCase();
  return {
    groupKey: `${status}|${candidateKey}`,
    candidateKey,
    displayName,
    normalizedName: normalized,
    status,
    suppressionReason,
    suggestedEntityKind: determineSuggestedCaseEntityKind(displayName, match),
    match,
    hit: {
      sourceField,
      sheetRowNumber: rowIndex + 1,
      pathLabel: normalizeCell(row?.[0]),
      transactionHash: normalizeCell(row?.[1]),
      excerpt: normalizeCell(rawPhrase)
    }
  };
}

export function mergeCaseEntityObservation(groupsByKey, observation) {
  if (!observation) return;
  let group = groupsByKey.get(observation.groupKey);
  if (!group) {
    group = {
      id: `entity-group-${caseBuddyHashString(observation.groupKey)}`,
      candidateKey: observation.candidateKey,
      displayName: observation.displayName,
      normalizedName: observation.normalizedName,
      status: observation.status,
      isReviewable: observation.status === CASE_ENTITY_REVIEW_STATUS_NEW,
      isKnownMatch: observation.status === CASE_ENTITY_REVIEW_STATUS_KNOWN,
      isSuppressed: observation.status === CASE_ENTITY_REVIEW_STATUS_SUPPRESSED,
      suggestedEntityKind: observation.suggestedEntityKind,
      suggestedKindLabel: formatCaseEntityKindLabel(observation.suggestedEntityKind),
      suppressionReason: observation.suppressionReason,
      matchedCanonicalName: observation.match?.matchedName || '',
      matchedEntityKind: observation.match?.entityKind || '',
      matchedKindLabel: formatCaseEntityKindLabel(observation.match?.entityKind || ''),
      matchedSource: observation.match?.source || '',
      isCaseMismatch: !!observation.match?.isCaseMismatch,
      caseMismatchName: observation.match?.matchedName || '',
      caseMismatchColumnJHitCount: 0,
      canApplyCaseMismatchCasingFix: false,
      caseMismatchActionSummary: '',
      hitCount: 0,
      sourceTerms: [],
      badges: [],
      hits: [],
      searchText: '',
      suggestedSummary: ''
    };
    groupsByKey.set(observation.groupKey, group);
  }

  if (!group.sourceTerms.includes(observation.displayName)) group.sourceTerms.push(observation.displayName);
  group.hits.push(observation.hit);
}

export function finalizeCaseEntityGroup(group = {}) {
  group.hitCount = Array.isArray(group.hits) ? group.hits.length : 0;
  group.caseMismatchColumnJHitCount = group.isCaseMismatch
    ? (Array.isArray(group.hits) ? group.hits.filter(hit => normalizeCell(hit?.sourceField).toLowerCase() === 'system notes').length : 0)
    : 0;
  group.canApplyCaseMismatchCasingFix = !!group.isCaseMismatch && !!group.caseMismatchName && group.caseMismatchColumnJHitCount > 0;
  group.caseMismatchActionSummary = group.canApplyCaseMismatchCasingFix
    ? `Use “Match curator casing” to update ${group.caseMismatchColumnJHitCount} Column J note${group.caseMismatchColumnJHitCount === 1 ? '' : 's'} to the curator spelling “${group.caseMismatchName}”.`
    : group.isCaseMismatch
      ? `This looks like curator term “${group.caseMismatchName}”, but there is no editable Column J note in this card.`
      : '';
  group.badges = [];

  if (group.isReviewable) {
    group.badges.push(group.isCaseMismatch ? 'Known name with different casing' : 'Candidate new entity');
    group.badges.push(group.suggestedKindLabel || 'Unknown');
    group.suggestedSummary = group.isCaseMismatch
      ? `${group.hitCount} hit${group.hitCount === 1 ? '' : 's'} look similar to known curator term “${group.caseMismatchName}”, but the case/spelling does not match exactly.`
      : `${group.hitCount} hit${group.hitCount === 1 ? '' : 's'} may need a local curator decision.`;
  } else if (group.isKnownMatch) {
    group.badges.push(`Known match: ${group.matchedCanonicalName}`);
    group.badges.push(group.matchedKindLabel || 'Known');
    group.suggestedSummary = `${group.hitCount} hit${group.hitCount === 1 ? '' : 's'} matched the current effective curator.`;
  } else {
    group.badges.push('Suppressed');
    if (group.suppressionReason) group.badges.push(group.suppressionReason);
    group.suggestedSummary = `${group.hitCount} hit${group.hitCount === 1 ? '' : 's'} suppressed as non-entity noise.`;
  }

  const searchParts = [
    group.displayName,
    group.normalizedName,
    group.matchedCanonicalName,
    group.caseMismatchName,
    group.suppressionReason,
    ...(group.sourceTerms || []),
    ...(group.hits || []).flatMap(hit => [hit.pathLabel, hit.transactionHash, hit.excerpt])
  ];
  group.searchText = searchParts.map(item => normalizeCell(item).toLowerCase()).filter(Boolean).join(' ');
  return group;
}

export function compareCaseEntityGroups(left = {}, right = {}) {
  const rank = group => group.isReviewable ? 0 : group.isKnownMatch ? 1 : 2;
  const rankDiff = rank(left) - rank(right);
  if (rankDiff !== 0) return rankDiff;
  const hitDiff = (right.hitCount || 0) - (left.hitCount || 0);
  if (hitDiff !== 0) return hitDiff;
  return normalizeCell(left.displayName).localeCompare(normalizeCell(right.displayName));
}

export function buildCaseEntityReviewBasisHash(groups = []) {
  const reviewable = (Array.isArray(groups) ? groups : [])
    .filter(group => group.isReviewable)
    .map(group => ({
      displayName: group.displayName,
      normalizedName: group.normalizedName,
      suggestedEntityKind: group.suggestedEntityKind,
      hits: (group.hits || []).map(hit => ({
        row: hit.sheetRowNumber,
        source: hit.sourceField,
        pathLabel: hit.pathLabel,
        hash: hit.transactionHash,
        excerpt: hit.excerpt
      }))
    }));
  return `entity-review-${caseBuddyHashString(JSON.stringify(reviewable))}`;
}

export function buildCaseEntityCuratorReviewAnalysis(mainData = [], remote = {}, overlay = {}, reviewRecord = null) {
  const effective = buildEffectiveEntityCurators(remote, overlay);
  const groupsByKey = new Map();
  const rows = normalizeCaseMatrix(mainData);

  rows.forEach((row, rowIndex) => {
    if (rowIndex === 0 || !rowHasMeaningfulData(row)) return;
    const attribution = normalizeCell(row?.[10]);
    if (attribution && !/^unknown$/i.test(attribution)) {
      mergeCaseEntityObservation(groupsByKey, evaluateCaseEntityObservation(row, rowIndex, 'Attribution', attribution, effective, overlay));
    }

    extractCaseEntityNoteCandidateFragments(row?.[9]).forEach((fragment) => {
      mergeCaseEntityObservation(groupsByKey, evaluateCaseEntityObservation(row, rowIndex, 'System Notes', fragment, effective, overlay));
    });
  });

  const groups = Array.from(groupsByKey.values()).map(finalizeCaseEntityGroup).sort(compareCaseEntityGroups);
  const reviewableGroups = groups.filter(group => group.isReviewable);
  const knownGroups = groups.filter(group => group.isKnownMatch);
  const suppressedGroups = groups.filter(group => group.isSuppressed);
  const currentBasisHash = buildCaseEntityReviewBasisHash(groups);
  const confirmedBasisHash = normalizeCell(reviewRecord?.basisHash);
  const isCurrent = !!currentBasisHash && !!confirmedBasisHash && currentBasisHash === confirmedBasisHash;

  let summary = '';
  if (reviewableGroups.length === 0) {
    summary = 'No reviewable new entity candidates are currently visible in Main Data.';
  } else {
    summary = `${reviewableGroups.length} reviewable candidate${reviewableGroups.length === 1 ? '' : 's'} need curator review.`;
  }
  if (knownGroups.length > 0) summary += ` ${knownGroups.length} group${knownGroups.length === 1 ? '' : 's'} already match the effective curator.`;
  if (suppressedGroups.length > 0) summary += ` ${suppressedGroups.length} group${suppressedGroups.length === 1 ? '' : 's'} suppressed as noise.`;

  return {
    effective,
    groups,
    reviewableGroups,
    knownGroups,
    suppressedGroups,
    totalGroupCount: groups.length,
    reviewableGroupCount: reviewableGroups.length,
    knownGroupCount: knownGroups.length,
    suppressedGroupCount: suppressedGroups.length,
    currentBasisHash,
    confirmedBasisHash,
    isCurrent,
    summary,
    remoteExchangeCount: effective.remoteExchangeCount,
    remoteNonExchangeCount: effective.remoteNonExchangeCount,
    localExchangeAdditions: effective.overlay.exchangeAdditions.length,
    localNonExchangeAdditions: effective.overlay.nonExchangeAdditions.length,
    localExchangeRemovals: effective.overlay.exchangeRemovals.length,
    localNonExchangeRemovals: effective.overlay.nonExchangeRemovals.length,
    localSuppressedTerms: effective.overlay.suppressedTerms.length
  };
}

export function buildEntityCuratorAuditRows(analysis = {}, actionLabel = 'Entity curator review') {
  return [
    ['Case Buddy Known Entity Curator Audit', '', '', '', '', '', '', '', '', 'Generated by Review new entities. Audit only; local curator changes are stored in browser local storage and do not modify server/admin curators.'],
    ['Action', actionLabel, 'Reviewable', analysis.reviewableGroupCount || 0, 'Known', analysis.knownGroupCount || 0, 'Suppressed', analysis.suppressedGroupCount || 0, 'Basis', analysis.currentBasisHash || ''],
    ['Status', 'Display Name', 'Suggested Kind', 'Hits', 'First Row', 'First Path Label', 'First Hash', 'Summary', 'Source Terms'],
    ...((analysis.groups || []).map((group) => {
      const firstHit = group.hits?.[0] || {};
      return [
        group.status || '',
        group.displayName || '',
        group.suggestedKindLabel || '',
        group.hitCount || 0,
        firstHit.sheetRowNumber || '',
        firstHit.pathLabel || '',
        firstHit.transactionHash || '',
        group.suggestedSummary || '',
        (group.sourceTerms || []).join(', ')
      ];
    }))
  ];
}
